import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { creativesAPI } from './creativesAPI';
import { handleSnackbar } from '@/shared/ui';
import type { BrandFolderFile, CreativeFilterDef } from '@/entities/creatives';

export interface CreativesState {
  creatives: {
    headers: any[];
    rows: any[];
    totalItems: number;
    page: number;
    rowsPerPage: number;
    search?: string;
  };
  creativeFilters: CreativeFilterDef[];
  selectedCreativeFilters: Record<string, any>;
  selectedCreatives: any[]; // Add selectedCreatives state
  // Modal states - one state per modal
  creativeDetailsModal: {
    open: boolean;
    selectedCreative: any | null;
    context?: 'view' | 'edit' | 'create';
  };
  selectCreativeModal: {
    open: boolean;
    selectedCreatives: BrandFolderFile[];
    actionOnSave: boolean;
    selectionMode: 'multiple' | 'single';
  };
  addCreativeModal: {
    open: boolean;
    type: undefined | 'default' | 'custom';
  };
  deleteCreativeModal: {
    open: boolean;
    selectedCreative: any | null;
  };
  brandCreativeModal: {
    open: boolean;
    selectedBrand: any | null;
  };
  imagePreviewModal: string | null;
  loading: boolean;
}

const initialState: CreativesState = {
  creatives: {
    headers: [],
    rows: [],
    totalItems: 0,
    page: 0,
    rowsPerPage: 10,
    search: '',
  },
  creativeFilters: [],
  selectedCreativeFilters: {},
  selectedCreatives: [], // Add selectedCreatives to initial state
  creativeDetailsModal: {
    open: false,
    selectedCreative: null,
    context: undefined,
  },
  selectCreativeModal: {
    open: false,
    selectedCreatives: [],
    actionOnSave: false,
    selectionMode: 'multiple',
  },
  addCreativeModal: {
    open: false,
    type: 'default',
  },
  deleteCreativeModal: {
    open: false,
    selectedCreative: null,
  },
  brandCreativeModal: {
    open: false,
    selectedBrand: null,
  },
  imagePreviewModal: null,
  loading: false,
};

// Async thunk for fetching creatives
export const getCreatives = createAsyncThunk(
  'creatives/getCreatives',
  async (params: any, { dispatch }) => {
    try {
      const response = await creativesAPI.fetchCreatives(params);
      return response;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch creatives';
      dispatch(
        handleSnackbar({
          open: true,
          message,
          severity: 'error',
          autoHideDuration: 3000,
        })
      );
      throw error;
    }
  }
);

// Async thunk for fetching creative filters
export const getCreativeFilters = createAsyncThunk(
  'creatives/getCreativeFilters',
  async (_, { dispatch }) => {
    try {
      const response = await creativesAPI.fetchCreativeFilters();
      return response;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to fetch creative filters';
      dispatch(
        handleSnackbar({
          open: true,
          message,
          severity: 'error',
          autoHideDuration: 3000,
        })
      );
      throw error;
    }
  }
);

// Async thunk for uploading creative
export const uploadCreative = createAsyncThunk(
  'creatives/uploadCreative',
  async (payload: any, { dispatch }) => {
    try {
      const files = Object.entries(payload.src).reduce(
        (acc: Record<string, any>, [ratio, item]: [string, any]) => {
          if (item?.src) {
            const [width, height] = item.resolution
              ? item.resolution.split('x').map(Number)
              : [0, 0];

            acc[ratio] = {
              aspectRatio: ratio,
              src: item.src,
              size: parseFloat(item.size) || 0,
              type: item.src.endsWith('.mp4') ? 'VIDEO' : 'IMAGE',
              resolution: {
                width,
                height,
              },
            };
          }
          return acc;
        },
        {}
      );

      const body = {
        name: payload.name,
        description: payload.description || '',
        duration: payload.duration || 0,
        files,
      };

      const response = await creativesAPI.uploadCreative(body);

      // Refresh creatives list after successful upload
      dispatch(getCreatives({}));

      dispatch(
        handleSnackbar({
          open: true,
          message: 'Creative uploaded successfully',
          severity: 'success',
          autoHideDuration: 2000,
        })
      );
      return response;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to upload creative';
      dispatch(
        handleSnackbar({
          open: true,
          message,
          severity: 'error',
          autoHideDuration: 3000,
        })
      );
      throw error;
    }
  }
);

// Async thunk for deleting creative
export const deleteCreative = createAsyncThunk(
  'creatives/deleteCreative',
  async (id: string | number, { dispatch }) => {
    try {
      const response = await creativesAPI.deleteCreative(id);

      if (response.success) {
        // Refresh creatives list after successful deletion
        dispatch(getCreatives({}));

        dispatch(
          handleSnackbar({
            open: true,
            message: 'Creative deleted successfully',
            severity: 'success',
            autoHideDuration: 2000,
          })
        );
        return response;
      }
      throw new Error('Failed to delete creative');
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to delete creative';
      dispatch(
        handleSnackbar({
          open: true,
          message,
          severity: 'error',
          autoHideDuration: 3000,
        })
      );
      throw error;
    }
  }
);

const creativesSlice = createSlice({
  name: 'creatives',
  initialState,
  reducers: {
    setCreatives: (
      state,
      action: PayloadAction<Partial<CreativesState['creatives']>>
    ) => {
      state.creatives = { ...state.creatives, ...action.payload };
    },
    setCreativeFilters: (state, action: PayloadAction<CreativeFilterDef[]>) => {
      state.creativeFilters = action.payload;
    },
    setSelectedCreativeFilters: (
      state,
      action: PayloadAction<Record<string, any>>
    ) => {
      state.selectedCreativeFilters = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setPagination: (
      state,
      action: PayloadAction<{ page?: number; rowsPerPage?: number }>
    ) => {
      if (action.payload.page !== undefined) {
        state.creatives.page = action.payload.page;
      }
      if (action.payload.rowsPerPage !== undefined) {
        state.creatives.rowsPerPage = action.payload.rowsPerPage;
      }
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.creatives.search = action.payload;
    },
    // Creative Details Modal
    setCreativeDetailsModal: (
      state,
      action: PayloadAction<{
        open: boolean;
        selectedCreative?: any;
        context?: 'view' | 'edit' | 'create';
      }>
    ) => {
      state.creativeDetailsModal.open = action.payload.open;
      if (action.payload.selectedCreative !== undefined) {
        state.creativeDetailsModal.selectedCreative =
          action.payload.selectedCreative;
      }
      if (action.payload.context !== undefined) {
        state.creativeDetailsModal.context = action.payload.context;
      }
    },
    // Select Creative Modal
    setSelectCreativeModal: (
      state,
      action: PayloadAction<{
        open: boolean;
        selectedCreatives?: BrandFolderFile[];
        actionOnSave?: boolean;
        selectionMode?: 'multiple' | 'single';
      }>
    ) => {
      state.selectCreativeModal.open = action.payload.open;
      if (action.payload.selectedCreatives !== undefined) {
        state.selectCreativeModal.selectedCreatives =
          action.payload.selectedCreatives;
      }
      if (action.payload.actionOnSave !== undefined) {
        state.selectCreativeModal.actionOnSave = action.payload.actionOnSave;
      }
      if (action.payload.selectionMode !== undefined) {
        state.selectCreativeModal.selectionMode = action.payload.selectionMode;
      } else if (!action.payload.open) {
        state.selectCreativeModal.selectionMode = 'multiple';
      }
    },
    // Add Creative Modal
    setAddCreativeModal: (
      state,
      action: PayloadAction<{
        open: boolean;
        type?: undefined | 'default' | 'custom';
        autoSelectAfterUpload?: boolean;
      }>
    ) => {
      state.addCreativeModal.open = action.payload.open;
    },
    // Delete Creative Modal
    setDeleteCreativeModal: (
      state,
      action: PayloadAction<{
        open: boolean;
        selectedCreative?: any | null;
      }>
    ) => {
      state.deleteCreativeModal.open = action.payload.open;
      if (action.payload.selectedCreative !== undefined) {
        state.deleteCreativeModal.selectedCreative =
          action.payload.selectedCreative;
      }
    },
    // Brand Creative Modal
    setBrandCreativeModal: (
      state,
      action: PayloadAction<{
        open: boolean;
        selectedBrand?: any | null;
      }>
    ) => {
      state.brandCreativeModal.open = action.payload.open;
      if (action.payload.selectedBrand !== undefined) {
        state.brandCreativeModal.selectedBrand = action.payload.selectedBrand;
      }
    },
    // Image Preview Modal
    setImagePreviewModal: (state, action: PayloadAction<string | null>) => {
      state.imagePreviewModal = action.payload;
    },
    // Selected Creatives
    setSelectedCreatives: (state, action: PayloadAction<any[]>) => {
      state.selectedCreatives = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getCreatives.pending, state => {
        state.loading = true;
      })
      .addCase(getCreatives.fulfilled, (state, action) => {
        state.loading = false;
        state.creatives = { ...state.creatives, ...action.payload };
      })
      .addCase(getCreatives.rejected, state => {
        state.loading = false;
      })
      .addCase(getCreativeFilters.fulfilled, (state, action) => {
        state.creativeFilters = action.payload;
      })
      .addCase(uploadCreative.pending, state => {
        state.loading = true;
      })
      .addCase(uploadCreative.fulfilled, state => {
        state.loading = false;
      })
      .addCase(uploadCreative.rejected, state => {
        state.loading = false;
      })
      .addCase(deleteCreative.pending, state => {
        state.loading = true;
      })
      .addCase(deleteCreative.fulfilled, state => {
        state.loading = false;
      })
      .addCase(deleteCreative.rejected, state => {
        state.loading = false;
      });
  },
});

export const {
  setCreatives,
  setCreativeFilters,
  setSelectedCreativeFilters,
  setLoading,
  setPagination,
  setSearch,
  setCreativeDetailsModal,
  setSelectCreativeModal,
  setAddCreativeModal,
  setDeleteCreativeModal,
  setBrandCreativeModal,
  setImagePreviewModal,
  setSelectedCreatives,
} = creativesSlice.actions;

export default creativesSlice.reducer;
